/** @jsxImportSource theme-ui */
import { useTranslation } from 'react-i18next';
import React from 'react';
import { Box } from 'theme-ui';
import { Link } from 'gatsby';
import { useBreakpointIndex } from '@theme-ui/match-media';
import Text from '../Text/Text';
import BtnPlay from '../../../assets/btnPlay.inline.svg';
import useTrainingAccess from '../../../hooks/useTrainingAccess';

interface VideoSessionItemProps {
  mode: 'horizontal' | 'vertical';
  title: string;
  content: string;
  thumbnailUrl: string;
  thumbnailHqUrl: string;
  duration: string;
  instructor: string;
  instructorId: string;
  hideInstructor: boolean;
  target: string;
  className?: string;
  markedAsPersonalized?: boolean;
  fullWidth?: boolean;
}

const VideoSessionItem: React.FC<VideoSessionItemProps> = ({
  mode = 'vertical',
  title,
  content,
  thumbnailUrl,
  thumbnailHqUrl,
  duration,
  instructor,
  instructorId,
  hideInstructor,
  target,
  className,
  markedAsPersonalized = false,
  fullWidth,
}: VideoSessionItemProps) => {
  const { t } = useTranslation();
  const { isLocked, handleTrainingClick } = useTrainingAccess();

  const breakpointIndex = useBreakpointIndex();

  // Default values for vertical mode
  let headlineLevel = 6;
  let flexDirection: 'column' | 'row' = 'column';
  let videoWidth = '15rem';
  let videoMinWidth = 'none';
  let videoMaxWidth = 'none';
  let videoAspectRatio = `${(136 / 240) * 100}%`;
  let textBoxPadding = { py: 2, px: 2 };
  let contentClamp = 2;

  if (mode === 'horizontal') {
    // We need to set the headline level to lowest on mobile resolution
    headlineLevel = 6;
    flexDirection = 'row';
    videoWidth = '15rem';
    videoMinWidth = '165px';
    videoMaxWidth = '45%';
    videoAspectRatio = `${(145 / 260) * 100}%`;
    textBoxPadding = { px: 3, py: 2 };
    contentClamp = breakpointIndex < 3 ? 1 : 2;
  }

  if (fullWidth) {
    videoWidth = '100%';
  }

  /* eslint-disable react/jsx-pascal-case */
  return (
    <div className={className}>
      <div
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection,
          a: {
            textDecoration: 'none',
          },
          filter: isLocked ? 'grayscale(1)' : undefined,
          opacity: isLocked ? 0.55 : 1,
        }}
      >
        <div
          sx={{
            width: videoWidth,
            maxWidth: videoMaxWidth,
            minWidth: videoMinWidth,
            flexShrink: 0,
          }}
        >
          <div
            sx={{
              position: 'relative',
              paddingTop: videoAspectRatio,
              borderRadius: '8px',
              boxShadow: '0px 10px 16px 0px rgba(0, 0, 0, 0.1)',
              backgroundColor: 'white',
              transition: 'transform ease-in 0.15s, box-shadow ease-in 0.15s',
              overflow: 'hidden',
              ':hover': {
                transform: 'scale(0.975)',
                boxShadow: '0px 10px 20px 5px rgba(0, 0, 0, 0.2)',
              },
            }}
          >
            {markedAsPersonalized && (
              <Text
                variant="default"
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 3,
                  px: 3,
                  py: 1,
                  zIndex: 3,
                  fontFamily: 'heading',
                  textTransform: 'uppercase',
                  fontSize: '10px',
                  fontWeight: 'bold',
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  borderRadius: '0 0 4px 4px',
                  pointerEvents: 'none',
                }}
              >
                {t('F_R_DICH')}
              </Text>
            )}
            <picture
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                objectFit: 'cover',
                objectPosition: 'center center',
              }}
            >
              <source srcSet={`${thumbnailUrl} 1x, ${thumbnailHqUrl} 2x`} />
              <img src={thumbnailUrl} alt={title} sx={{ width: '100%' }} />
            </picture>
            <div
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: '2rem',
                background:
                  'linear-gradient(-180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.4) 100%)',
                borderRadius: '0 0 8px 8px',
                width: '100%',
              }}
            />
            <Text
              sx={{
                fontFamily: 'heading',
                fontWeight: 'normal',
                fontSize: '12px',
                color: 'white',
                position: 'absolute',
                bottom: '8px',
                right: '8px',
                lineHeight: '20px',
              }}
            >
              {duration}
            </Text>

            <Box
              sx={{
                position: 'absolute',
                bottom: '2px',
                left: '2px',
                transformOrigin: 'bottom left',
                lineHeight: 1,
                width: '32px',
                height: '32px',
              }}
            >
              <BtnPlay sx={{ width: '32px', height: '32px' }} />
            </Box>

            <Link
              to={target}
              onClick={handleTrainingClick}
              aria-label={isLocked ? `${title} — subscription required` : title}
              sx={{
                position: 'absolute',

                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
              }}
            />
          </div>
        </div>
        <div sx={{ width: '100%', ...textBoxPadding }}>
          <Link to={target} onClick={handleTrainingClick}>
            <Text
              className="test-headline"
              variant={`h${headlineLevel}`}
              sx={{ maxHeight: '', mt: 0 }}
              clampLines={1}
            >
              {title}
            </Text>
            <Text
              variant={`h${headlineLevel}light`}
              sx={{
                mt: 1,
                minHeight: mode === 'vertical' ? '34px' : undefined,
              }}
              clampLines={contentClamp}
            >
              {content}
            </Text>
          </Link>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            {!hideInstructor && (
              <Link to={`/app/instructor-detail/${instructorId}`}>
                <Text
                  variant={`h${headlineLevel}light`}
                  sx={{ color: 'rgba(0,0,0,0.4)', mt: 2 }}
                >
                  {instructor}
                </Text>
              </Link>
            )}
          </Box>
        </div>
      </div>
    </div>
  );
};

export default VideoSessionItem;
